import { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Split, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { formatCurrency } from '@/lib/formatters';
import {
  useChartOfAccounts,
  useTransactionSplits,
  useUpdateTransactionSplits,
  type TransactionSplit,
  type TransactionSplitInput,
} from '@/features/financials/hooks';
import {
  buildCoaHierarchyByType,
  findLockedAccountIds,
  formatQbAccountLabel,
  QB_GROUP_ORDER,
} from '@/features/financials/coaHierarchy';

/**
 * Manual category-splits editor. Renders next to the QB Category card on
 * /financials/transactions/:id and toggles between two states:
 *
 *   • Off (no splits saved on the row) — collapses to a one-line
 *     "Split this transaction across categories" toggle. The single-
 *     category picker above is the active control.
 *   • On — replaces the single-category picker conceptually: each split
 *     row is amount + category + optional per-line description. A
 *     live "Remaining" indicator shows the gap between the split sum
 *     and the parent transaction.amount_cents. Save persists via the
 *     `set_transaction_splits` RPC, which does the sum + category-
 *     existence checks server-side and rolls back on any failure.
 *
 * Splits beat single-category at post time: the edge function emits a
 * multi-line Purchase (debit) / Deposit (credit) when at least one
 * split exists, ignoring the parent's qb_account_id.
 *
 * Locked while the row is mid-post (`qb_post_status === 'posting'`) and
 * grays out for bank-failed / in_qb rows. We deliberately allow editing
 * splits on a posted row — the "Update QuickBooks" button down the
 * page will overwrite the booked Purchase / Deposit with the new lines.
 */
interface Props {
  /** Atlas transaction id. */
  transactionId: string;
  /** Total transaction amount in cents (always positive — direction
   *  carries the sign elsewhere). The sum of split amounts must equal
   *  this exactly before the operator can save. */
  amountCents: number;
  /** Disabled while the row is mid-post or bank-failed / in_qb. The
   *  parent page computes this and passes it through. */
  disabled: boolean;
  /** Disabled-with-explanation reason. Surfaced in a small helper line
   *  above the editor. Pass null when not disabled. */
  disabledReason: string | null;
}

export function TransactionSplitsCard({
  transactionId,
  amountCents,
  disabled,
  disabledReason,
}: Props) {
  const { data: coa } = useChartOfAccounts();
  const { data: serverSplits, isLoading } = useTransactionSplits(transactionId);
  const updateSplits = useUpdateTransactionSplits();

  // CoA picker grouping — same helpers the QB Category dropdown uses,
  // so the operator gets the same hierarchical look + locked-parent
  // handling on both surfaces.
  const qbAccountsByType = useMemo(() => buildCoaHierarchyByType(coa), [coa]);
  const lockedAccountIds = useMemo(() => findLockedAccountIds(coa), [coa]);

  // Local edit state — separate from server cache so the operator can
  // edit several rows without each keystroke hitting the network. We
  // sync from the server snapshot when the row id changes or the
  // server splits actually change (e.g. a sibling tab saved). The ref
  // lets us tell "true server change" from "we just saved and got the
  // same data back" — without it, our own save would overwrite an
  // in-progress edit.
  const [enabled, setEnabled] = useState<boolean>(false);
  const [drafts, setDrafts] = useState<DraftSplit[]>([]);
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  const [hydratedFromServerVersion, setHydratedFromServerVersion] = useState<string>('');
  const serverVersion = useMemo(
    () => splitsSignature(serverSplits ?? []),
    [serverSplits],
  );

  useEffect(() => {
    // Re-hydrate when (a) we navigate to a different transaction or
    // (b) the server-side splits actually changed shape (someone else
    // saved). We deliberately do NOT re-hydrate on every render — the
    // operator's in-progress edits would get overwritten.
    const idChanged = hydratedFor !== transactionId;
    const versionChanged = hydratedFromServerVersion !== serverVersion;
    if (!idChanged && !versionChanged) return;
    if (isLoading) return;

    const next = (serverSplits ?? []).map((s, i) => ({
      tempId: `srv-${s.id}`,
      lineNumber: i + 1,
      qbAccountId: s.qb_account_id,
      amountCents: s.amount_cents,
      description: s.description ?? '',
    }));
    setDrafts(next);
    setEnabled(next.length > 0);
    setHydratedFor(transactionId);
    setHydratedFromServerVersion(serverVersion);
  }, [
    transactionId,
    serverSplits,
    serverVersion,
    isLoading,
    hydratedFor,
    hydratedFromServerVersion,
  ]);

  const splitTotalCents = useMemo(
    () => drafts.reduce((acc, d) => acc + (Number.isFinite(d.amountCents) ? d.amountCents : 0), 0),
    [drafts],
  );
  const remainingCents = amountCents - splitTotalCents;

  // Save is enabled when:
  //   • there are at least 2 splits (a single split is just a single-
  //     category transaction with extra steps; the operator should
  //     untick the toggle instead),
  //   • every split has a category assigned,
  //   • every split has amount > 0,
  //   • the splits sum exactly to the parent amount.
  const canSave =
    enabled &&
    drafts.length >= 2 &&
    drafts.every((d) => !!d.qbAccountId && d.amountCents > 0) &&
    remainingCents === 0;

  // Toggling the master switch off (with at least one saved split)
  // queues a "clear" save — the operator confirms by clicking Save,
  // which sends an empty array to the RPC. Toggling on with no draft
  // splits seeds two empty rows so the form has something to render.
  const onToggleEnabled = (next: boolean) => {
    setEnabled(next);
    if (next && drafts.length === 0) {
      // Two seeded rows: one for the full amount (operator-friendly
      // starting point — they edit the first row down and a second
      // row absorbs the remainder), one empty so they don't have to
      // click Add immediately.
      setDrafts([
        {
          tempId: `new-${Date.now()}-1`,
          lineNumber: 1,
          qbAccountId: '',
          amountCents: amountCents,
          description: '',
        },
        {
          tempId: `new-${Date.now()}-2`,
          lineNumber: 2,
          qbAccountId: '',
          amountCents: 0,
          description: '',
        },
      ]);
    }
  };

  const onAddRow = () => {
    setDrafts((prev) => [
      ...prev,
      {
        tempId: `new-${Date.now()}-${prev.length + 1}`,
        lineNumber: prev.length + 1,
        qbAccountId: '',
        // Default new rows to whatever's currently un-allocated so the
        // operator's most-common workflow (split a $500 charge into
        // $50 Interest + $450 Loan principal: enter $50 on the first
        // row, click Add → second row pre-fills $450) takes one
        // click instead of two.
        amountCents: Math.max(0, remainingCents),
        description: '',
      },
    ]);
  };

  const onRemoveRow = (tempId: string) => {
    setDrafts((prev) =>
      prev
        .filter((d) => d.tempId !== tempId)
        .map((d, i) => ({ ...d, lineNumber: i + 1 })),
    );
  };

  const onUpdateRow = (tempId: string, patch: Partial<DraftSplit>) => {
    setDrafts((prev) =>
      prev.map((d) => (d.tempId === tempId ? { ...d, ...patch } : d)),
    );
  };

  const onSave = async () => {
    try {
      if (enabled) {
        if (!canSave) {
          toast.error('Fix the splits before saving — see the indicators below.');
          return;
        }
        const payload: TransactionSplitInput[] = drafts.map((d) => ({
          qb_account_id: d.qbAccountId,
          amount_cents: d.amountCents,
          description: d.description.trim() ? d.description.trim() : null,
        }));
        await updateSplits.mutateAsync({ transactionId, splits: payload });
        toast.success(`Saved ${payload.length} splits`);
      } else {
        // Disable + Save = clear all splits. The RPC accepts an empty
        // array as the explicit "clear" signal (we never silently
        // delete rows out from under the operator).
        await updateSplits.mutateAsync({ transactionId, splits: [] });
        toast.success('Splits cleared — back to single-category posting');
      }
      // Re-hydrate from the server snapshot we just produced. Bump
      // the version so the effect above doesn't think someone
      // else changed things.
      setHydratedFromServerVersion('');
    } catch (e) {
      // Always log the raw error to the console so the operator can
      // copy/paste the full shape (code + details + hint) into a
      // bug report — the toast only has room for the headline.
      // eslint-disable-next-line no-console
      console.error('[TransactionSplitsCard] save failed', e);
      toast.error(formatSplitsError(e));
    }
  };

  // Server-side state: are there saved splits? Mirrors `serverSplits.length > 0`
  // but read from the dedicated state so we can tell "edit + clear" apart from
  // "no splits to begin with".
  const hasServerSplits = (serverSplits ?? []).length > 0;
  // When we're enabled and the operator has unsaved changes (drafts diverge
  // from the server snapshot), surface a "you have unsaved changes" hint.
  const draftsSignature = useMemo(() => draftsToSignature(drafts), [drafts]);
  const hasUnsavedChanges = enabled
    ? draftsSignature !== serverVersion
    : hasServerSplits; // unticking the toggle while saved splits exist is itself an unsaved change

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <Split className="h-4 w-4 text-atmosphere-dark" />
              Split across multiple categories
            </CardTitle>
            {disabled && disabledReason && (
              <p className="pt-1 text-xs text-muted-foreground">{disabledReason}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Label
              htmlFor={`splits-toggle-${transactionId}`}
              className="cursor-pointer text-xs text-muted-foreground"
            >
              {enabled ? 'On' : 'Off'}
            </Label>
            <Switch
              id={`splits-toggle-${transactionId}`}
              checked={enabled}
              onCheckedChange={onToggleEnabled}
              disabled={disabled || isLoading}
            />
          </div>
        </div>
      </CardHeader>
      {enabled && (
        <CardContent className="space-y-3">
          {/* Header row labels — kept on its own grid so the inputs
              line up cleanly across rows even when the description
              wraps to two lines. */}
          <div className="grid grid-cols-12 gap-2 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <div className="col-span-5">Category</div>
            <div className="col-span-3 text-right">Amount</div>
            <div className="col-span-3">Description (optional)</div>
            <div className="col-span-1" />
          </div>

          <div className="space-y-2">
            {drafts.map((d) => (
              <SplitRow
                key={d.tempId}
                draft={d}
                disabled={disabled}
                qbAccountsByType={qbAccountsByType}
                lockedAccountIds={lockedAccountIds}
                onUpdate={(patch) => onUpdateRow(d.tempId, patch)}
                onRemove={() => onRemoveRow(d.tempId)}
                canRemove={drafts.length > 1}
              />
            ))}
          </div>

          {/* Sum + Remaining indicator. Renders three colours:
              moss-green when the splits balance (canSave-eligible),
              salmon when we're over the parent amount, atmosphere
              (info blue) when we're under. */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm">
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onAddRow}
                disabled={disabled}
                className="h-auto px-2 py-1 text-xs"
              >
                <Plus className="h-3.5 w-3.5" />
                Add split
              </Button>
              <span className="text-xs text-muted-foreground">
                {drafts.length} line{drafts.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 tabular-nums">
              <span className="text-xs text-muted-foreground">
                Sum:{' '}
                <span className="font-medium text-foreground">
                  {formatCurrency(splitTotalCents)}
                </span>
              </span>
              <span className="text-xs text-muted-foreground">
                Total: <span className="font-medium text-foreground">{formatCurrency(amountCents)}</span>
              </span>
              <span
                className={
                  'rounded px-2 py-0.5 text-xs font-medium ' +
                  (remainingCents === 0
                    ? 'bg-bright-moss-dark/15 text-bright-moss-dark'
                    : remainingCents < 0
                      ? 'bg-fresh-salmon-dark/15 text-fresh-salmon-dark'
                      : 'bg-atmosphere-dark/15 text-atmosphere-dark')
                }
              >
                {remainingCents === 0
                  ? 'Balanced'
                  : remainingCents > 0
                    ? `Remaining ${formatCurrency(remainingCents)}`
                    : `Over by ${formatCurrency(Math.abs(remainingCents))}`}
              </span>
            </div>
          </div>
        </CardContent>
      )}
      <CardContent className={enabled ? 'pt-0' : undefined}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {enabled && !canSave
              ? drafts.length < 2
                ? 'Add at least two splits.'
                : remainingCents === 0
                  ? 'Pick a category for every split.'
                  : remainingCents > 0
                    ? `Add ${formatCurrency(remainingCents)} more to balance.`
                    : `Reduce by ${formatCurrency(Math.abs(remainingCents))} to balance.`
              : null}
          </p>
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <span className="text-xs text-muted-foreground">Unsaved changes</span>
            )}
            <Button
              type="button"
              size="sm"
              variant={enabled ? 'default' : 'outline'}
              onClick={onSave}
              disabled={
                disabled ||
                updateSplits.isPending ||
                (enabled ? !canSave : !hasServerSplits) ||
                !hasUnsavedChanges
              }
            >
              {updateSplits.isPending ? (
                <>
                  <Save className="h-3.5 w-3.5 animate-pulse" />
                  Saving…
                </>
              ) : enabled ? (
                <>
                  <Save className="h-3.5 w-3.5" />
                  Save splits
                </>
              ) : (
                <>
                  <X className="h-3.5 w-3.5" />
                  Clear splits
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// SplitRow — a single editable row in the splits editor.
// -----------------------------------------------------------------------------

interface DraftSplit {
  /** Stable key so React doesn't reorder inputs when we renumber. The
   *  value is `srv-<id>` for server-loaded rows and `new-<...>` for
   *  client-added rows; not used at the API layer. */
  tempId: string;
  /** 1-based index for display + the line_number we send to the RPC. */
  lineNumber: number;
  qbAccountId: string;
  amountCents: number;
  description: string;
}

function SplitRow({
  draft,
  disabled,
  qbAccountsByType,
  lockedAccountIds,
  onUpdate,
  onRemove,
  canRemove,
}: {
  draft: DraftSplit;
  disabled: boolean;
  qbAccountsByType: ReturnType<typeof buildCoaHierarchyByType>;
  lockedAccountIds: Set<string>;
  onUpdate: (patch: Partial<DraftSplit>) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  // Local, free-form amount string so the operator can type "12" / "12." /
  // "12.50" and edit fluidly without each keystroke being clamped /
  // rounded by the cents-int model. We sync to / from cents on blur and
  // when the parent updates the row externally (e.g. Add Split's
  // remaining-cents seeding).
  const [amountInput, setAmountInput] = useState<string>(
    formatAmountInput(draft.amountCents),
  );
  useEffect(() => {
    setAmountInput(formatAmountInput(draft.amountCents));
    // Run only when the parent row's amount changes. The operator's own
    // typing into amountInput updates `draft.amountCents` on blur via
    // commitAmount(), and that re-fires this effect with the same value —
    // but `formatAmountInput(cents)` is a pure function so the local
    // state lands the same string and React skips the render.
  }, [draft.amountCents]);

  const commitAmount = () => {
    const cents = parseAmountInput(amountInput);
    if (cents === null) {
      // Parse failure: snap back to the last good value so the input
      // never ends in an unparseable state. Surface a small visual
      // tell via the Sum / Remaining indicator's "Over by …" badge.
      setAmountInput(formatAmountInput(draft.amountCents));
      return;
    }
    onUpdate({ amountCents: cents });
  };

  return (
    <div className="grid grid-cols-12 items-start gap-2">
      <div className="col-span-5">
        <Select
          value={draft.qbAccountId || ''}
          onValueChange={(v) => onUpdate({ qbAccountId: v })}
          disabled={disabled}
        >
          <SelectTrigger className="h-9 w-full">
            <SelectValue placeholder="Pick a category…" />
          </SelectTrigger>
          <SelectContent>
            {QB_GROUP_ORDER.map(({ key, label }) => {
              const items = qbAccountsByType.get(key) ?? [];
              if (items.length === 0) return null;
              return (
                <SelectGroup key={key}>
                  <SelectSeparator />
                  <SelectLabel>{label}</SelectLabel>
                  {items.map(({ account, depth, locked }) => (
                    <SelectItem
                      key={account.id}
                      value={account.id}
                      disabled={locked || lockedAccountIds.has(account.id)}
                      className={
                        locked
                          ? 'py-2 pl-8 leading-6 text-muted-foreground/60 data-[disabled]:opacity-100'
                          : 'py-2 pl-8 leading-6'
                      }
                      style={{ paddingLeft: `${2 + depth * 1.25}rem` }}
                    >
                      {formatQbAccountLabel(account)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              );
            })}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-3">
        <Input
          inputMode="decimal"
          className="h-9 text-right tabular-nums"
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value)}
          onBlur={commitAmount}
          disabled={disabled}
          placeholder="0.00"
        />
      </div>
      <div className="col-span-3">
        <Input
          className="h-9"
          value={draft.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          disabled={disabled}
          placeholder="e.g. Interest portion"
        />
      </div>
      <div className="col-span-1 flex items-center justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          disabled={disabled || !canRemove}
          aria-label="Remove split"
          className="h-9 w-9 text-muted-foreground hover:text-fresh-salmon-dark"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// helpers
// -----------------------------------------------------------------------------

/**
 * Stable string signature for a list of splits so we can detect "the
 * server changed" vs "we still have the same data" without doing a deep
 * compare on every render. Includes line_number so reordering counts as
 * a change.
 */
function splitsSignature(splits: TransactionSplit[]): string {
  return splits
    .map(
      (s) =>
        `${s.line_number}:${s.qb_account_id}:${s.amount_cents}:${s.description ?? ''}`,
    )
    .join('|');
}

function draftsToSignature(drafts: DraftSplit[]): string {
  return drafts
    .map(
      (d) =>
        `${d.lineNumber}:${d.qbAccountId}:${d.amountCents}:${d.description.trim()}`,
    )
    .join('|');
}

/**
 * Render an integer-cents value as a free-form decimal string for the
 * editable input. Empty cents (0 on a freshly-added row) renders as ''
 * instead of '0.00' so the operator's first keystroke replaces the
 * placeholder rather than appearing after a 0.
 */
function formatAmountInput(cents: number): string {
  if (!Number.isFinite(cents) || cents === 0) return '';
  const dollars = (cents / 100).toFixed(2);
  return dollars;
}

/**
 * Parse the operator's free-form decimal input back to integer cents.
 * Tolerant of '$', ',', leading/trailing whitespace, and bare integers.
 * Returns null on parse failure (caller snaps back to the prior value).
 */
function parseAmountInput(input: string): number | null {
  const cleaned = input.trim().replace(/[$,\s]/g, '');
  if (cleaned === '') return 0;
  if (!/^[0-9]+(\.[0-9]{1,2})?$/.test(cleaned)) return null;
  const dollars = Number.parseFloat(cleaned);
  if (!Number.isFinite(dollars) || dollars < 0) return null;
  return Math.round(dollars * 100);
}

/**
 * Surface the most useful single line we can from whatever error shape
 * Supabase / postgres / the network throws at us.
 *
 * The naive `e instanceof Error ? e.message : 'Save failed'` pattern
 * we used originally fails on the most common case here: PostgREST
 * returns `PostgrestError`, which is a *plain object*
 * (`{ message, details, hint, code }`) — not an `Error` instance —
 * so `instanceof Error` is `false` and the toast shows the literal
 * fallback string with no insight at all. We pull `message` directly
 * (and append `details` when they add information) so the operator
 * sees e.g. "Splits sum to 49500 cents but the transaction is 50000
 * cents — they must match exactly" instead of a useless "Save failed".
 */
function formatSplitsError(e: unknown): string {
  if (typeof e === 'string') return e || 'Save failed';
  if (e instanceof Error) return e.message || 'Save failed';
  if (e && typeof e === 'object') {
    const obj = e as Record<string, unknown>;
    const message = typeof obj.message === 'string' ? obj.message.trim() : '';
    const details = typeof obj.details === 'string' ? obj.details.trim() : '';
    const hint = typeof obj.hint === 'string' ? obj.hint.trim() : '';
    const code = typeof obj.code === 'string' ? obj.code.trim() : '';
    // Postgres permission denials surface as `42501` with a vague
    // `permission denied for ...` message; pair the message with the
    // hint when present so the operator gets the actionable next step.
    const headline = message || details || hint;
    if (headline) {
      const suffix = details && details !== headline ? ` (${details})` : '';
      const codeSuffix = code && !headline.includes(code) ? ` [${code}]` : '';
      return headline + suffix + codeSuffix;
    }
  }
  return 'Save failed';
}
